import { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { Share2, Copy, Check, Mail, Trash2, ExternalLink, X } from "lucide-react";

export default function ShareDialog({ rattrapageId, rattrapageTitle, open, onClose }) {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [profEmail, setProfEmail] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await api.post(`/rattrapages/${rattrapageId}/share`);
        setToken(data.share_token);
      } catch (e) {
        setErr(formatError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, rattrapageId]);

  if (!open) return null;

  const publicUrl = token ? `${window.location.origin}/preview/${token}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setErr("Impossible d'accéder au presse-papiers");
    }
  };

  const mailtoHref = () => {
    const subject = `Relecture rattrapage – ${rattrapageTitle}`;
    const body =
      `Bonjour,\n\nPourriez-vous relire le sujet de rattrapage ci-dessous et nous transmettre vos retours ?\n\n` +
      `${publicUrl}\n\n` +
      `Le document est en lecture seule. Vous pouvez afficher ou masquer les bonnes réponses depuis l'aperçu.\n\nMerci d'avance,\nL'équipe pédagogique`;
    const to = profEmail.trim();
    return `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const revoke = async () => {
    if (!window.confirm("Révoquer ce lien ? Le professeur ne pourra plus accéder à l'aperçu.")) return;
    try {
      await api.delete(`/rattrapages/${rattrapageId}/share`);
      setToken("");
      onClose();
    } catch (e) {
      setErr(formatError(e));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="share-dialog" onClick={onClose}>
      <div
        className="bg-white border border-slate-200 rounded-md shadow-xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Share2 size={16} className="text-[#2563eb]" />
            <div className="font-display font-semibold text-slate-900">Partager pour relecture</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="share-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <p className="text-sm text-slate-600">
            Générez un lien de relecture et transmettez-le au professeur. Il aura un aperçu complet du QCM en
            lecture seule, et pourra masquer les bonnes réponses à la demande.
          </p>

          {loading ? (
            <div className="text-sm text-slate-500" data-testid="share-loading">Génération du lien…</div>
          ) : err ? (
            <div className="text-sm text-[#b91c1c] bg-[#fef2f2] border border-red-200 px-3 py-2 rounded-sm">{err}</div>
          ) : (
            <>
              <div>
                <label className="label-xs block mb-2">Lien d'aperçu</label>
                <div className="flex items-stretch gap-2">
                  <input
                    readOnly
                    value={publicUrl}
                    data-testid="share-link-input"
                    className="flex-1 px-3 py-2 text-xs text-slate-700 border border-slate-300 rounded-sm bg-slate-50 font-mono"
                  />
                  <button
                    type="button"
                    onClick={copyLink}
                    data-testid="copy-link-btn"
                    className="inline-flex items-center gap-1.5 bg-[#0f172a] text-white px-3 text-sm font-semibold rounded-sm hover:bg-slate-800"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? "Copié" : "Copier"}
                  </button>
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="open-preview-btn"
                    className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-800 px-3 text-sm font-semibold rounded-sm hover:bg-slate-50"
                    title="Ouvrir l'aperçu"
                  >
                    <ExternalLink size={14} />
                  </a>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-5">
                <label className="label-xs block mb-2">Envoyer par e-mail au professeur</label>
                <div className="flex items-stretch gap-2">
                  <input
                    type="email"
                    placeholder="professeur@ecole.fr (optionnel)"
                    value={profEmail}
                    onChange={(e) => setProfEmail(e.target.value)}
                    data-testid="share-email-input"
                    className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-sm focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                  />
                  <a
                    href={mailtoHref()}
                    data-testid="share-mailto-btn"
                    className="inline-flex items-center gap-1.5 bg-[#2563eb] text-white px-3 text-sm font-semibold rounded-sm hover:bg-blue-700"
                  >
                    <Mail size={14} /> Ouvrir mon mail
                  </a>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  L'app ouvrira votre client mail (Outlook, Gmail, etc.) avec un message pré-rempli contenant le lien.
                  Le destinataire est optionnel — vous pouvez le saisir directement dans votre messagerie.
                </p>
              </div>

              <div className="border-t border-slate-200 pt-4 flex items-center justify-between">
                <button
                  type="button"
                  onClick={revoke}
                  data-testid="revoke-share-btn"
                  className="inline-flex items-center gap-1.5 text-xs text-[#b91c1c] hover:underline"
                >
                  <Trash2 size={12} /> Révoquer le lien
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm text-slate-600 hover:text-slate-900"
                  data-testid="share-done-btn"
                >
                  Terminé
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
